import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import type { Session } from '@supabase/supabase-js';
import type { UserProfile } from '@bcc/shared';
import { ROLES } from '@bcc/shared';
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { clearEventCornerDismissStateForUser } from '../constants/eventCornerDismiss';

type AuthCtx = {
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  role: typeof ROLES[keyof typeof ROLES];
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /**
   * After `signInWithPassword`, React state can lag behind Supabase. Updates session + profile
   * with `flushSync` so `/dashboard` sees auth on the next paint (avoids RequireAuth → /login bounce).
   */
  syncAfterSignIn: (session: Session) => Promise<UserProfile | null>;
};

const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshProfileForToken = useCallback(async (accessToken?: string | null) => {
    if (!accessToken) {
      setProfile(null);
      return;
    }
    try {
      const me = await apiFetch<{ user: UserProfile }>('/api/me', { token: accessToken });
      setProfile(me.user);
    } catch {
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const s = data.session ?? null;
      setSession(s);
      refreshProfileForToken(s?.access_token).finally(() => {
        setLoading(false);
      });
    });
    const sub = supabase.auth.onAuthStateChange((_evt, next) => {
      setSession(next);
      void refreshProfileForToken(next?.access_token);
      setLoading(false);
    });
    return () => {
      mounted = false;
      sub.data.subscription.unsubscribe();
    };
  }, [refreshProfileForToken]);

  const signOut = useCallback(async () => {
    const uid = session?.user?.id;
    await supabase.auth.signOut();
    if (uid) clearEventCornerDismissStateForUser(uid);
    setProfile(null);
  }, [session?.user?.id]);

  const refreshProfile = useCallback(async () => {
    await refreshProfileForToken(session?.access_token);
  }, [session?.access_token, refreshProfileForToken]);

  const syncAfterSignIn = useCallback(async (next: Session): Promise<UserProfile | null> => {
    flushSync(() => {
      setSession(next);
    });
    try {
      const me = await apiFetch<{ user: UserProfile }>('/api/me', { token: next.access_token });
      flushSync(() => {
        setProfile(me.user);
      });
      return me.user;
    } catch {
      flushSync(() => {
        setProfile(null);
      });
      return null;
    }
  }, []);

  const role = profile?.role ?? ROLES.INVESTOR;

  const value = useMemo<AuthCtx>(
    () => ({
      session,
      profile,
      loading,
      role,
      signOut,
      refreshProfile,
      syncAfterSignIn,
    }),
    [session, profile, loading, role, signOut, refreshProfile, syncAfterSignIn]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth requires AuthProvider');
  return v;
}
